import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useClientCtx } from '../../context/ClientContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, UserCircle, Calendar, CalendarClock, Clock, FileText,
  AlertTriangle, CheckSquare, BarChart3, Settings,
  ChevronDown, X, Pill, ClipboardList,
  MessageSquare, CalendarOff, Building2, Briefcase, ChevronsRight, ChevronsLeft,
  UsersRound, ShieldCheck, Navigation, Navigation2, Bot, DollarSign,
  Heart, Target, Receipt, Home, FileCheck, User, UserPlus, Scale, Search,
  Globe, Star, Car, Database, LineChart, HardDrive,
  HeartHandshake, Compass, Mail,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFeatures, useModules } from '../../context/FeatureContext';
import api from '../../utils/api';
import { useNavIndex, useRegisterNavItem, searchNav } from './navIndex';
import { SECTION_HUE } from './routes';
import BranchSwitcher from './BranchSwitcher';
import { StatusPill } from '../ui/StatusPill';
import clsx from 'clsx';

// Restraint: every section header is the same quiet neutral, and the single accent (indigo) is
// reserved for the item you are on — colour means "you are here" (Linear / Attio / Notion).
// Section labels are slate-400 on the dark rail, which keeps small caps above 4.5:1.

// Rows are 36px on a desktop with a mouse; under a finger they keep the 44px from .sidebar-item.
const DENSE = '[@media(min-width:768px)_and_(pointer:fine)]:min-h-[36px] [@media(min-width:768px)_and_(pointer:fine)]:py-1.5';
const DENSE_SUB = '[@media(min-width:768px)_and_(pointer:fine)]:min-h-[32px] [@media(min-width:768px)_and_(pointer:fine)]:py-1.5';

// Which section a leaf sits in, so the nav index (sidebar search, the Ctrl-K palette and the page
// title fallback) can say "Finance & Payroll › Financials" instead of nothing.
const NavSectionCtx = createContext('');

const matches = (p, pathname) => (p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`));
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Destinations of a group's direct children, used to open the group you are standing in.
function childPaths(children) {
  const out = [];
  React.Children.forEach(children, (c) => {
    if (!React.isValidElement(c)) return;
    const p = c.props || {};
    if (p.item && p.item.path) out.push(p.item.path.split('?')[0]);
    if (p.basePath) out.push(p.basePath);
    if (p.also) out.push(...p.also);
    if (p.path) out.push(String(p.path).split('?')[0]);
    if (p.navPaths) out.push(...p.navPaths);
  });
  return out;
}

// The body of a section or group. When closed it stays MOUNTED but hidden, so every leaf the user
// is allowed to open still registers in the nav index: search, pins, recents and the Ctrl-K
// palette can find "Pay Groups" inside a closed Admin section. (They used to unmount, so anything
// in a collapsed section could not be searched and its recents silently dropped.) Only opening
// animates — a short fade.
function Reveal({ open, id, className, children }) {
  return (
    <div id={id} hidden={!open}>
      <motion.div initial={false} animate={{ opacity: open ? 1 : 0 }} transition={{ duration: 0.15, ease: 'easeOut' }} className={className}>
        {children}
      </motion.div>
    </div>
  );
}

// ─── Sub-item (leaf nav link) ─────────────────────────────────────────────────
function SubItem({ label, path, badge }) {
  const location = useLocation();
  const section = useContext(NavSectionCtx);
  useRegisterNavItem({ label, path, section });
  // Query-aware active state: sub-items that share a pathname but differ by ?tab= (Compliance)
  // must each highlight only on their exact URL.
  const [base, qs] = path.split('?');
  const isActive = qs
    ? (location.pathname + location.search) === path
    : (location.pathname === base && !location.search);
  return (
    <Link
      to={path}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-2 py-2.5 pl-3 pr-3 rounded-lg text-[13px] font-medium transition-colors duration-150 min-h-[40px]',
        DENSE_SUB,
        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      )}
    >
      <span className="flex-1 truncate">{label}</span>
      {badge !== undefined && (
        <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center tabular-nums',
          badge > 0 ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-300')}>
          {badge}
        </span>
      )}
    </Link>
  );
}

// ─── Plan Management leaf ─────────────────────────────────────────────────────
// PM drives every screen off `?view=`, and several carry a second param
// (?view=queues&queue=notifications, ?view=pm-reports&report=turnaround). SubItem
// compares the WHOLE query string, so those would lose their highlight the moment
// a sub-tab was picked — this compares only the view.
function PmSubItem({ label, view, badge }) {
  const location = useLocation();
  const current = new URLSearchParams(location.search).get('view') || 'dashboard';
  const isActive = location.pathname === '/plan-management' && current === view;
  const to = view === 'dashboard' ? '/plan-management' : `/plan-management?view=${view}`;
  useRegisterNavItem({ label, path: to, section: 'Plan Management' });
  return (
    <Link to={to} aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-2 py-2.5 pl-3 pr-3 rounded-lg text-[13px] font-medium transition-colors duration-150 min-h-[40px]',
        DENSE_SUB,
        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      )}>
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center bg-red-500 text-white tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

const SUB_LABEL = 'px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400';

// The Plan Management rail, now living in the main navigation instead of a second
// sidebar inside the page. The counts are fetched ONLY while the operator is
// actually in Plan Management: three extra calls on every load of every other
// screen is a real cost for a number nobody is looking at.
function PlanManagementNav() {
  const location = useLocation();
  const onPm = location.pathname.startsWith('/plan-management');
  const [badge, setBadge] = useState({ mail: 0, downloads: 0, alerts: 0 });

  useEffect(() => {
    if (!onPm) return undefined;
    let alive = true;
    const put = (key, pick) => (r) => { if (alive) setBadge((b) => ({ ...b, [key]: pick(r.data) || 0 })); };
    api.get('/plan-management/mail/unread-count').then(put('mail', (d) => d?.unread)).catch(() => {});
    api.get('/plan-management/downloads/unread-count').then(put('downloads', (d) => d?.unread)).catch(() => {});
    api.get('/plan-management/sentinel').then(put('alerts', (d) => d?.total)).catch(() => {});
    return () => { alive = false; };
    // Re-reads on every move inside PM, so approving an invoice or opening the
    // downloads queue is reflected without a page reload.
  }, [onPm, location.search]);

  return (
    <>
      <PmSubItem label="Dashboard"       view="dashboard" />
      <PmSubItem label="Invoices"        view="service-entry" />
      <PmSubItem label="New Invoice"     view="entry" />
      <PmSubItem label="Mailbox"         view="mail"     badge={badge.mail} />
      <PmSubItem label="Review Queues"   view="queues" />
      <PmSubItem label="Client List"     view="participants" />

      <p className={SUB_LABEL}>Claiming</p>
      <PmSubItem label="NDIA Claims"     view="claims" />
      <PmSubItem label="Pay Providers"   view="payments" />
      <PmSubItem label="Reconcile"       view="reconcile" />
      <PmSubItem label="Downloads"       view="downloads" badge={badge.downloads} />

      <p className={SUB_LABEL}>Insights</p>
      <PmSubItem label="Alerts"          view="alerts"   badge={badge.alerts} />
      <PmSubItem label="Audit & Reports" view="reports" />
      <PmSubItem label="Reports"         view="pm-reports" />
      <PmSubItem label="Registers"       view="registers" />
      <PmSubItem label="Powerview"       view="powerview" />

      <p className={SUB_LABEL}>Setup</p>
      <PmSubItem label="Service Providers" view="providers" />
      <PmSubItem label="Statements"      view="statements" />
      <PmSubItem label="Settings"        view="settings" />
    </>
  );
}

// ─── Support Coordination leaf ────────────────────────────────────────────────
// Same shape as PmSubItem and for the same reason: SC drives its screens off `?view=`, and a leaf
// that compared the whole query string would lose its highlight the moment a screen set a second
// param (an open engagement adds ?tab=).
function ScSubItem({ label, view, badge }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const current = params.get('view') || 'overview';
  // A setup tool (?tool=) open over the screen lights up Settings instead; a participant's file
  // (/support-coordination/<id>) sits under Caseload.
  const inFile = location.pathname.startsWith('/support-coordination/');
  const isActive = inFile ? view === 'engagements'
    : location.pathname === '/support-coordination' && (params.get('tool') ? view === 'settings' : current === view);
  const to = view === 'overview' ? '/support-coordination' : `/support-coordination?view=${view}`;
  useRegisterNavItem({ label, path: to, section: 'Support Coordination' });
  return (
    <Link to={to} aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-2 py-2.5 pl-3 pr-3 rounded-lg text-[13px] font-medium transition-colors duration-150 min-h-[40px]',
        DENSE_SUB,
        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      )}>
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center bg-red-500 text-white tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

// The Support Coordination rail, in the main navigation rather than a tab strip inside the page —
// same treatment as Plan Management, so the two modules are operated the same way.
function SupportCoordinationNav() {
  const location = useLocation();
  const onSc = location.pathname.startsWith('/support-coordination');
  const [badge, setBadge] = useState({ today: 0, referrals: 0 });

  useEffect(() => {
    if (!onSc) return undefined;
    let alive = true;
    api.get('/support-coordination/my-day')
      .then((r) => { if (alive) setBadge((b) => ({ ...b, today: (r.data?.tasks || r.data?.items || []).length || 0 })); })
      .catch(() => {});
    // SC workflow v1 (4): the Referrals leaf carries the count of open referrals.
    api.get('/sc/referrals/counters')
      .then((r) => { if (alive) setBadge((b) => ({ ...b, referrals: Number(r.data?.open_total) || 0 })); })
      .catch(() => {});
    return () => { alive = false; };
  }, [onSc, location.search]);

  return (
    <>
      {/* The menu follows the owner's spec (section 4): company-level screens here; a
          participant's Notes & Billing, Plan & Budgets, Referrals, Communications and SC Hours
          open inside their file. Today and Insights live inside Overview; Calendar has its own
          leaf (appointments are booked there). */}
      <ScSubItem label="Overview"  view="overview" badge={badge.today} />
      <ScSubItem label="Caseload"  view="engagements" />
      <ScSubItem label="Calendar"  view="calendar" />
      <ScSubItem label="Email"     view="email" />
      <ScSubItem label="Referrals" view="referrals" badge={badge.referrals} />
      <ScSubItem label="SC Hours"  view="hours" />
      <ScSubItem label="Claims"    view="claims" />
      <ScSubItem label="Settings"  view="settings" />
    </>
  );
}

// ─── Email leaf ───────────────────────────────────────────────────────────────
// Email drives its screens off `?view=` too: Inbox is the bare /mail (and every mail view inside it,
// such as ?view=waiting), Calendar, Tasks and Settings have their own view.
const EMAIL_VIEWS = ['calendar', 'tasks', 'settings'];
function EmailSubItem({ label, view, badge }) {
  const location = useLocation();
  const raw = new URLSearchParams(location.search).get('view');
  const current = EMAIL_VIEWS.includes(raw) ? raw : 'inbox';
  const isActive = location.pathname === '/mail' && current === view;
  const to = view === 'inbox' ? '/mail' : `/mail?view=${view}`;
  useRegisterNavItem({ label, path: to, section: 'Email' });
  return (
    <Link to={to} aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-2 py-2.5 pl-3 pr-3 rounded-lg text-[13px] font-medium transition-colors duration-150 min-h-[40px]',
        DENSE_SUB,
        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      )}>
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center bg-red-500 text-white tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

// The Email rail: mail, the company calendar, tasks and mail settings in one place. The Inbox count
// is kept fresh everywhere (every two minutes); the tasks count only while in Email.
function EmailNav() {
  const location = useLocation();
  const onMail = location.pathname === '/mail';
  const [badge, setBadge] = useState({ inbox: 0, tasks: 0 });
  useEffect(() => {
    let alive = true;
    const unread = () => api.get('/mail/accounts')
      .then((r) => { if (alive) setBadge((b) => ({ ...b, inbox: (r.data || []).reduce((t, a) => t + (a.folders || []).filter((f) => f.role === 'inbox').reduce((s, f) => s + (Number(f.unread) || 0), 0), 0) })); })
      .catch(() => {});
    unread();
    const t = setInterval(unread, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [location.pathname]);
  useEffect(() => {
    if (!onMail) return undefined;
    let alive = true;
    const today = new Date(); const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    api.get('/email-tasks', { params: { scope: 'mine', state: 'open' } })
      .then((r) => { if (alive) setBadge((b) => ({ ...b, tasks: (r.data || []).filter((x) => x.due_date && String(x.due_date).slice(0, 10) <= ymd).length })); })
      .catch(() => {});
    return () => { alive = false; };
  }, [onMail, location.search]);
  return (
    <>
      <EmailSubItem label="Inbox"    view="inbox" badge={badge.inbox} />
      <EmailSubItem label="Calendar" view="calendar" />
      <EmailSubItem label="Tasks"    view="tasks" badge={badge.tasks} />
      <EmailSubItem label="Settings" view="settings" />
    </>
  );
}

// ─── Aged Care leaf ───────────────────────────────────────────────────────────
// Aged Care drives its screens off `?tab=` (the overview is the bare /aged-care). Same shape as
// PmSubItem: compare only the tab, so a screen that adds a second param keeps its highlight.
function AcSubItem({ label, tab }) {
  const location = useLocation();
  const raw = new URLSearchParams(location.search).get('tab') || 'dashboard';
  const current = raw === 'participant' ? 'participants' : raw;   // a participant's file sits under Participants
  const isActive = location.pathname === '/aged-care' && current === tab;
  const to = tab === 'dashboard' ? '/aged-care' : `/aged-care?tab=${tab}`;
  useRegisterNavItem({ label, path: to, section: 'Aged Care' });
  return (
    <Link to={to} aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-2 py-2.5 pl-3 pr-3 rounded-lg text-[13px] font-medium transition-colors duration-150 min-h-[40px]',
        DENSE_SUB,
        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      )}>
      <span className="flex-1 truncate">{label}</span>
    </Link>
  );
}

// The Aged Care rail — the same groups, order and names the page used for its own tab strip.
function AgedCareNav() {
  return (
    <>
      <AcSubItem label="Overview"          tab="dashboard" />

      <p className={SUB_LABEL}>Participants</p>
      <AcSubItem label="Referrals"         tab="referrals" />
      <AcSubItem label="Quotes"            tab="quotes" />
      <AcSubItem label="Participants"      tab="participants" />
      <AcSubItem label="Client funding"    tab="funding" />
      <AcSubItem label="SAH budgets"       tab="budgets" />
      <AcSubItem label="Care management"   tab="caremgmt" />
      <AcSubItem label="Care plans"        tab="careplans" />
      <AcSubItem label="Client leave"      tab="leave" />

      <p className={SUB_LABEL}>Money</p>
      <AcSubItem label="Statements"        tab="statements" />
      <AcSubItem label="Claims"            tab="claims" />
      <AcSubItem label="Items & equipment" tab="items" />
      <AcSubItem label="Supplier invoices" tab="invoices" />
      <AcSubItem label="Providers & POs"   tab="providers" />

      <p className={SUB_LABEL}>Compliance</p>
      <AcSubItem label="SIRS incidents"    tab="sirs" />
      <AcSubItem label="Complaints"        tab="complaints" />
      <AcSubItem label="Worker compliance" tab="compliance" />
      <AcSubItem label="Government reports" tab="reports" />

      <p className={SUB_LABEL}>CHSP</p>
      <AcSubItem label="CHSP"              tab="chsp" />
      <AcSubItem label="DEX"               tab="dex" />

      <p className={SUB_LABEL}>Setup</p>
      <AcSubItem label="Service list"      tab="services" />
      <AcSubItem label="Rates & fees"      tab="rates" />
    </>
  );
}

// ─── Expandable sub-group ─────────────────────────────────────────────────────
function SubGroup({ label, children, defaultOpen = false }) {
  const { pathname } = useLocation();
  const section = useContext(NavSectionCtx);
  const hasActive = childPaths(children).some((p) => matches(p, pathname));
  const [open, setOpen] = useState(defaultOpen || hasActive);
  const bodyId = `nav-group-${slug(`${section}-${label}`)}`;

  // Open the group you navigate into (by search, a link inside a page, the back button).
  useEffect(() => { if (hasActive) setOpen(true); }, [pathname, hasActive]);

  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={bodyId}
        className={clsx('w-full flex items-center gap-2 py-2.5 pl-3 pr-3 rounded-lg text-[13px] font-medium text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors duration-150 min-h-[40px]', DENSE_SUB)}>
        <span className="flex-1 text-left truncate">{label}</span>
        <ChevronDown size={12} aria-hidden="true" className={clsx('transition-transform duration-150 shrink-0', open && 'rotate-180')} />
      </button>
      <NavSectionCtx.Provider value={section ? `${section} › ${label}` : label}>
        <Reveal open={open} id={bodyId} className="pl-3 space-y-0.5">{children}</Reveal>
      </NavSectionCtx.Provider>
    </div>
  );
}

// ─── Expandable top-level item ────────────────────────────────────────────────
// `also` adds paths that belong to the group without sharing its prefix (Billing under Settings);
// `exclude` removes ones that have their own item elsewhere (Pay Contractors under /financials).
function ExpandableNavItem({ label, icon: Icon, basePath, also = [], exclude = [], children, collapsed }) {
  const { pathname } = useLocation();
  const section = useContext(NavSectionCtx);
  const isActive = [basePath, ...also].some((p) => matches(p, pathname)) && !exclude.some((p) => matches(p, pathname));
  const [open, setOpen] = useState(isActive);
  const effectiveOpen = isActive || open;
  const bodyId = `nav-item-${slug(`${section}-${label}`)}`;

  useEffect(() => { if (isActive) setOpen(true); }, [isActive]);

  // In the icon-only rail the group's pages stay mounted (hidden), so they are still searchable
  // from the Ctrl-K palette while the rail is collapsed.
  const body = (
    <NavSectionCtx.Provider value={section ? `${section} › ${label}` : label}>
      <Reveal open={!collapsed && effectiveOpen} id={bodyId}
        className="ml-2 pl-2 border-l border-white/10 mt-0.5 mb-1 space-y-0.5">
        {children}
      </Reveal>
    </NavSectionCtx.Provider>
  );

  if (collapsed) {
    return (
      <>
        <NavLink to={basePath} aria-label={label} title={label}
          className={clsx('sidebar-item justify-center', DENSE, isActive && 'active')}>
          <Icon size={17} className="shrink-0" aria-hidden="true" />
        </NavLink>
        {body}
      </>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={effectiveOpen} aria-controls={bodyId}
        className={clsx('sidebar-item w-full', DENSE, isActive && 'active')}>
        <Icon size={17} className="shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left text-sm truncate">{label}</span>
        <ChevronDown size={13} aria-hidden="true" className={clsx('transition-transform duration-150 shrink-0', effectiveOpen && 'rotate-180')} />
      </button>
      {body}
    </div>
  );
}

// ─── Regular nav item ─────────────────────────────────────────────────────────
// `item.activeWhen(pathname)` overrides the highlight when a link's own URL match is too broad or
// too narrow (All Clients: on for the list and its filtered views, off on a client's own pages).
function NavItem({ item, collapsed }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  const section = useContext(NavSectionCtx);
  useRegisterNavItem({ label: item.label, path: item.path, section, icon: item.icon });
  return (
    <NavLink to={item.path} end={item.end ?? item.path === '/'}
      aria-label={collapsed ? item.label : undefined}
      // The collapsed rail clips anything drawn outside it, so its label is the native tooltip.
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => clsx('sidebar-item group relative', DENSE, collapsed && 'justify-center',
        (item.activeWhen ? item.activeWhen(pathname) : isActive) && 'active')}>
      <Icon size={17} className="shrink-0" aria-hidden="true" />
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.span initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }} className="overflow-hidden whitespace-nowrap text-sm">
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
    </NavLink>
  );
}

// ─── Department section wrapper ───────────────────────────────────────────────
function DepartmentSection({ label, children, collapsed, defaultOpen = true }) {
  const { pathname } = useLocation();
  // Collapsing a section you never use is only worth doing if it stays collapsed across reloads.
  const storeKey = `nav.section.${label}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storeKey);
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = () => setOpen((o) => {
    const next = !o;
    try { localStorage.setItem(storeKey, next ? '1' : '0'); } catch { /* private browsing */ }
    return next;
  });
  const hasActive = childPaths(children).some((p) => matches(p, pathname));
  // Arriving on a page inside a closed section opens it (without changing the saved choice), so
  // the highlighted item is always visible; the user can close it again.
  useEffect(() => { if (hasActive) setOpen(true); }, [pathname, hasActive]);

  // A section whose every item is gated off for this org says nothing — show nothing.
  if (React.Children.toArray(children).length === 0) return null;
  const bodyId = `nav-section-${slug(label)}`;

  return (
    <NavSectionCtx.Provider value={label}>
      {collapsed ? (
        <div className={clsx('space-y-0.5 nav-hued', `hue-${SECTION_HUE[label] || 'slate'}`)}>
          <div className="h-0.5 w-6 mx-auto my-2 rounded-full nav-dot opacity-60" aria-hidden="true" />
          {children}
        </div>
      ) : (
        <div className={clsx('mt-2 nav-hued', `hue-${SECTION_HUE[label] || 'slate'}`)}>
          <button type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-slate-400 hover:text-slate-200 transition-colors duration-150">
            <span className="w-1.5 h-1.5 rounded-full nav-dot shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left text-[10.5px] font-semibold uppercase tracking-[0.08em]">{label}</span>
            <ChevronDown size={12} aria-hidden="true" className={clsx('transition-transform duration-150 shrink-0', open && 'rotate-180')} />
          </button>
          <Reveal open={open} id={bodyId} className="space-y-0.5 pb-1">{children}</Reveal>
        </div>
      )}
    </NavSectionCtx.Provider>
  );
}

// ─── Contextual client sub-nav ────────────────────────────────────────────────
function ClientNavLink({ label, path, icon: Icon, end: isEnd = true }) {
  return (
    <NavLink to={path} end={isEnd}
      className={({ isActive }) => clsx(
        'flex items-center gap-2.5 py-2 px-3 rounded-lg text-[13px] font-medium transition-colors duration-150 min-h-[40px]',
        DENSE_SUB,
        isActive ? 'bg-primary-500/15 text-primary-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
      )}>
      <Icon size={14} className="shrink-0" aria-hidden="true" />
      <span className="truncate flex-1">{label}</span>
    </NavLink>
  );
}

const CLIENT_KEYWORDS = new Set(['new', 'archived', 'documents', 'teams']);

// The client's real record status, when the page has told us. Clients are either active or
// archived (is_active); nothing is shown rather than a guess while it is unknown.
const clientStatus = (c) => {
  if (!c) return null;
  if (c.status) return String(c.status).toLowerCase();
  if (c.is_active === true) return 'active';
  if (c.is_active === false) return 'archived';
  return null;
};

function ClientSubNav({ cid }) {
  const { activeClient } = useClientCtx() || {};
  const features = useFeatures() || {};
  // Only trust the context when it is about THIS client (it lags a render behind a navigation).
  const ctx = activeClient && String(activeClient.id) === String(cid) ? activeClient : null;
  const name = ctx?.name || null;
  const status = clientStatus(ctx);
  const parts = (name || '').trim().split(/\s+/);
  const initials = name ? `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase() : '';

  return (
    <div className="mt-1 mb-2 ml-2 pl-2 border-l border-white/10">
      <div className="mb-1.5 flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
        <span className="w-7 h-7 rounded-full bg-slate-700 text-slate-100 text-[10px] font-semibold flex items-center justify-center shrink-0" aria-hidden="true">
          {initials || <User size={13} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-white truncate">{name || <span className="text-slate-400 font-normal">Loading…</span>}</p>
          {status && <StatusPill status={status} className="mt-0.5 !px-1.5 !py-0 !text-[10.5px]" />}
        </div>
      </div>

      <ClientNavLink label="Overview"       path={`/clients/${cid}`}               icon={User} />
      <ClientNavLink label="Care Plan"      path={`/clients/${cid}/care-plan`}     icon={Heart} />
      <ClientNavLink label="Calendar"       path={`/clients/${cid}/calendar`}      icon={Calendar} />
      <ClientNavLink label="Documents"      path={`/clients/${cid}/documents`}     icon={FileText} />
      <ClientNavLink label="Communications" path={`/clients/${cid}/communications`} icon={MessageSquare} />
      <ClientNavLink label="Email"          path={`/clients/${cid}/email`}          icon={Mail} />

      <p className={SUB_LABEL}>Finance</p>
      <ClientNavLink label="Billing Report" path={`/clients/${cid}/billing-report`} icon={Receipt} />
      {/* Same gate as the route (App.jsx: enable_quotes), so the link never bounces. */}
      {features.enable_quotes !== false && (
        <ClientNavLink label="Quotes"       path={`/clients/${cid}/quotes/new`}     icon={FileCheck} end={false} />
      )}

      <p className={SUB_LABEL}>Engagement</p>
      <ClientNavLink label="Goals"        path={`/clients/${cid}/goals`}         icon={Target} end={false} />
      <ClientNavLink label="Forms"        path={`/clients/${cid}/forms`}         icon={ClipboardList} end={false} />
      <ClientNavLink label="Family Portal" path={`/clients/${cid}/family-portal`} icon={Home} />
    </div>
  );
}

// ─── Clients (inside the People section) ──────────────────────────────────────
// All Clients stays in place on a client's own pages (it used to vanish there, leaving no way
// back to the list), with that client's pages nested under it.
const clientsListActive = (pathname) => pathname === '/clients' || /^\/clients\/(archived|teams|documents)(\/|$)/.test(pathname);

function ClientsContent({ collapsed }) {
  const { pathname } = useLocation();
  const cid = pathname.match(/^\/clients\/([^/]+)/)?.[1];
  const onDetail = !!cid && !CLIENT_KEYWORDS.has(cid);

  return (
    <>
      <NavItem item={{ label: 'All Clients', path: '/clients', icon: Users, end: true, activeWhen: clientsListActive }} collapsed={collapsed} />
      {onDetail && !collapsed && <ClientSubNav cid={cid} />}
      <NavItem item={{ label: 'New Client',  path: '/clients/new', icon: UserPlus }} collapsed={collapsed} />
    </>
  );
}

// ─── Quick-access row (search result, pin, or recent) ─────────────────────────
// One row shape for all three lists so they read as one thing. The star is always visible (it is
// the only way to pin, and nothing is hidden until hover).
const lastSection = (s) => String(s || '').split(' › ').pop();

function QuickRow({ entry, pinned, onTogglePin, onNavigate, showActive = false }) {
  const location = useLocation();
  const here = location.pathname + location.search;
  const active = showActive && (here === entry.path || (location.pathname === entry.path && !location.search));
  return (
    <div className={clsx('flex items-center rounded-lg', active ? 'bg-primary-500/15' : 'hover:bg-white/5')}>
      <Link to={entry.path} onClick={onNavigate} aria-current={active ? 'page' : undefined}
        className={clsx('flex-1 min-w-0 flex items-center gap-2 py-2 pl-3 pr-1 text-[13px] font-medium min-h-[36px]',
          active ? 'text-primary-300' : 'text-slate-300 hover:text-white')}>
        <span className="flex-1 min-w-0 truncate">{entry.label}</span>
        {entry.section && <span className="text-[10.5px] text-slate-400 truncate max-w-[92px] shrink-0">{lastSection(entry.section)}</span>}
      </Link>
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(entry.path); }}
        aria-label={`Pin ${entry.label}`} aria-pressed={!!pinned}
        title={pinned ? `Unpin ${entry.label}` : `Pin ${entry.label} to the top`}
        className={clsx('w-8 h-8 mr-0.5 rounded-md shrink-0 inline-flex items-center justify-center transition-colors duration-150',
          pinned ? 'text-slate-200 hover:text-white' : 'text-slate-500 hover:text-slate-200')}>
        <Star size={13} fill={pinned ? 'currentColor' : 'none'} aria-hidden="true" />
      </button>
    </div>
  );
}

// ─── Search + pins + recents ──────────────────────────────────────────────────
// Roughly 104 destinations sit below this. Type to find anything by name (including items inside
// closed sections), keep the handful you use daily at the top, and get back to where you just were.
// Recent never repeats the page you are on and is capped at three, so the tree below barely moves.
function NavQuickAccess({ collapsed, onNavigate }) {
  const nav = useNavIndex();
  const [q, setQ] = useState('');
  const location = useLocation();
  const here = location.pathname + location.search;
  const noteVisit = nav && nav.noteVisit;

  // Record where we have been. Runs on every navigation, including ones made from inside a page.
  useEffect(() => { if (noteVisit) noteVisit(here); }, [here, noteVisit]);

  if (collapsed || !nav) return null;

  const results = searchNav(nav.all, q);
  const byPath = new Map(nav.all.map((e) => [e.path, e]));
  const pinned = nav.pins.map((path) => byPath.get(path)).filter(Boolean);
  const recent = nav.recents
    .filter((path) => !nav.pins.includes(path) && path !== here)
    .map((path) => byPath.get(path))
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div className="px-1 pb-2 mb-1 border-b border-white/5">
      <div className="relative mb-1">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" aria-hidden="true" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setQ(''); }}
          placeholder="Search menu…" aria-label="Search the menu"
          className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-8 py-2 text-[13px] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/60" />
        {q && (
          <button type="button" onClick={() => setQ('')} aria-label="Clear search"
            className="absolute right-0.5 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-200">
            <X size={12} aria-hidden="true" />
          </button>
        )}
      </div>

      {q ? (
        results.length ? (
          <div className="space-y-0.5">
            {results.map((e) => (
              <QuickRow key={e.path} entry={e} pinned={nav.pins.includes(e.path)} showActive
                onTogglePin={nav.togglePin} onNavigate={() => { setQ(''); if (onNavigate) onNavigate(); }} />
            ))}
          </div>
        ) : (
          <p className="px-3 py-3 text-[12px] text-slate-400">Nothing matches “{q}”.</p>
        )
      ) : (
        <>
          {pinned.length > 0 && (
            <div className="space-y-0.5">
              <p className="px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Pinned</p>
              {pinned.map((e) => (
                <QuickRow key={e.path} entry={e} pinned onTogglePin={nav.togglePin} onNavigate={onNavigate} />
              ))}
            </div>
          )}
          {recent.length > 0 && (
            <div className="space-y-0.5">
              <p className="px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Recent</p>
              {recent.map((e) => (
                <QuickRow key={e.path} entry={e} pinned={false} onTogglePin={nav.togglePin} onNavigate={onNavigate} />
              ))}
            </div>
          )}
          {pinned.length === 0 && recent.length === 0 && (
            <p className="px-3 py-2 text-[12px] text-slate-400 leading-snug">
              Search any screen by name, then press its star to pin it here.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
// Sign out and the signed-in identity live in the header's account menu, so the rail no longer
// repeats them in a footer; collapse / expand stays at the top of the rail.
export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen, isMobile }) {
  const { user } = useAuth();
  const features = useFeatures();
  const modules = useModules();
  const mod = (k) => modules[k] !== false; // per-org module access (granted by super admin)
  const feat = (k) => features[k] !== false; // org page toggles (default shown unless turned off)

  const isAdmin = ['admin', 'office_user', 'super_admin'].includes(user?.role);
  const messagingEnabled = features.enable_general_chat || features.enable_team_chats || features.enable_direct_messaging;

  // On mobile drawer, always show expanded labels (not icon-only)
  const ec = isMobile ? false : collapsed;
  const closeDrawer = () => { if (isMobile) setMobileOpen(false); };

  return (
    <motion.aside
      animate={
        isMobile
          ? { x: mobileOpen ? 0 : -300, width: 280 }
          : { x: 0, width: collapsed ? 72 : 240 }
      }
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={clsx(
        'fixed top-0 left-0 h-screen flex flex-col overflow-hidden bg-slate-950 border-r border-white/5 print:hidden',
        isMobile ? 'z-50' : 'z-30'
      )}
    >
      {/* Logo + collapse/expand toggle. The organisation's name is carried by the switcher below. */}
      <div className={clsx('flex items-center h-16 border-b border-white/5 shrink-0', ec ? 'px-2 gap-1' : 'pl-4 pr-2')}>
        {ec ? (
          <>
            <div className="w-7 h-7 overflow-hidden flex items-center shrink-0">
              <img src="/logo-admin.png" alt="Rostery" className="h-7 w-auto max-w-none object-left" />
            </div>
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar" title="Expand sidebar"
              className="ml-auto w-9 h-9 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors duration-150 shrink-0 flex items-center justify-center">
              <ChevronsRight size={16} aria-hidden="true" />
            </button>
          </>
        ) : (
          <>
            <img src="/logo-admin.png" alt="Rostery" className="h-9 w-auto max-w-[180px] object-contain object-left" />
            <button
              type="button"
              onClick={() => (isMobile ? setMobileOpen(false) : setCollapsed(true))}
              aria-label={isMobile ? 'Close menu' : 'Collapse sidebar'}
              title={isMobile ? 'Close menu' : 'Collapse sidebar'}
              className="ml-auto w-9 h-9 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors duration-150 shrink-0 flex items-center justify-center">
              {isMobile ? <X size={16} aria-hidden="true" /> : <ChevronsLeft size={15} aria-hidden="true" />}
            </button>
          </>
        )}
      </div>

      {/* Above the navigation, because it changes what every item below it shows. */}
      <div className="px-2 pt-2">
        <BranchSwitcher collapsed={ec} />
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto py-2 px-2 scrollbar-thin space-y-0.5">

        <NavQuickAccess collapsed={ec} onNavigate={closeDrawer} />

        {/* ── DASHBOARD (standalone home) ────────────────────────────────── */}
        <div className="mb-1 nav-hued hue-indigo">
          <NavItem item={{ label: 'Dashboard', path: '/', icon: LayoutDashboard }} collapsed={ec} />
        </div>

        {/* ── SCHEDULING (items, order and gates unchanged — owner's decision) ── */}
        <DepartmentSection label="Scheduling" collapsed={ec}>
          <NavItem item={{ label: 'Scheduler',     path: '/scheduler',     icon: Calendar }}        collapsed={ec} />
          {feat('enable_templates') && <NavItem item={{ label: 'Templates',     path: '/templates',     icon: CalendarClock }}   collapsed={ec} />}
          {feat('enable_groups') && mod('group_services') && <NavItem item={{ label: 'Group Services', path: '/groups',       icon: UsersRound }}      collapsed={ec} />}
          {mod('job_board') && <NavItem item={{ label: 'Job Board',     path: '/job-board',     icon: Briefcase }}       collapsed={ec} />}
          {mod('gps_tracking') && <NavItem item={{ label: 'Tracking',      path: '/live-tracking', icon: Navigation }}      collapsed={ec} />}
          {mod('gps_tracking') && <NavItem item={{ label: 'EVV',           path: '/evv',           icon: ShieldCheck }}     collapsed={ec} />}
        </DepartmentSection>

        {/* ── PEOPLE (clients + staff together) ──────────────────────────── */}
        {isAdmin && (
          <DepartmentSection label="People" collapsed={ec}>
            <ClientsContent collapsed={ec} navPaths={['/clients']} />
            <ExpandableNavItem label="Staff" icon={UserCircle} basePath="/staff" collapsed={ec}>
              <SubItem label="All Staff" path="/staff" />
              <SubItem label="Archived"  path="/staff/archived" />
              <SubGroup label="Document Hub">
                <SubItem label="Shared"  path="/staff/documents/shared" />
                <SubItem label="Expired" path="/staff/documents/expired" />
              </SubGroup>
              <SubItem label="Contracts" path="/staff/contracts" />
              <SubItem label="New Staff" path="/staff/new" />
            </ExpandableNavItem>
            {feat('enable_teams') && <NavItem item={{ label: 'Teams', path: '/teams', icon: UsersRound }} collapsed={ec} />}
            {mod('leave') && <NavItem item={{ label: 'Leave', path: '/leave', icon: CalendarOff }} collapsed={ec} />}
          </DepartmentSection>
        )}

        {/* ── CARE & QUALITY ─────────────────────────────────────────────── */}
        <DepartmentSection label="Care & Quality" collapsed={ec}>
          {features.enable_emar && mod('medications') && (
            <NavItem item={{ label: 'Medications', path: '/medications', icon: Pill }} collapsed={ec} />
          )}
          {feat('enable_forms') && mod('forms') && <NavItem item={{ label: 'Forms', path: '/forms', icon: ClipboardList }} collapsed={ec} />}
          {features.enable_tasks && (
            <NavItem item={{ label: 'Tasks', path: '/tasks', icon: CheckSquare }} collapsed={ec} />
          )}
          {isAdmin && features.enable_incidents && (
            <NavItem item={{ label: 'Incidents', path: '/incidents', icon: AlertTriangle }} collapsed={ec} />
          )}
        </DepartmentSection>

        {/* ── AGED CARE (HCP / Support at Home) ─────────────────────────────── */}
        {isAdmin && mod('aged_care') && (
          <DepartmentSection label="Aged Care" collapsed={ec}>
            <ExpandableNavItem label="Aged Care" icon={HeartHandshake} basePath="/aged-care" collapsed={ec}>
              <AgedCareNav />
            </ExpandableNavItem>
          </DepartmentSection>
        )}

        {/* ── SUPPORT COORDINATION (NDIS CoS) ─────────────────────────────── */}
        {isAdmin && mod('support_coordination') && (
          <DepartmentSection label="Support Coordination" collapsed={ec}>
            <ExpandableNavItem label="Support Coordination" icon={Compass} basePath="/support-coordination" collapsed={ec}>
              <SupportCoordinationNav />
            </ExpandableNavItem>
          </DepartmentSection>
        )}

        {/* ── PLAN MANAGEMENT (provider invoices + NDIA claims) ──────────────── */}
        {isAdmin && mod('plan_management') && (
          <DepartmentSection label="Plan Management" collapsed={ec}>
            <ExpandableNavItem label="Plan Management" icon={FileText} basePath="/plan-management" collapsed={ec}>
              <PlanManagementNav />
            </ExpandableNavItem>
          </DepartmentSection>
        )}

        {/* ── FINANCE & PAYROLL (all money in one place) ─────────────────── */}
        {isAdmin && (
          <DepartmentSection label="Finance & Payroll" collapsed={ec}>
            {feat('enable_invoices') && <NavItem item={{ label: 'Invoices',       path: '/invoices',       icon: FileText }}   collapsed={ec} />}
            {feat('enable_timesheets') && <NavItem item={{ label: 'Timesheets',     path: '/timesheets',     icon: Clock }}      collapsed={ec} />}
            {feat('enable_payslips') && mod('schads_payroll') && <NavItem item={{ label: 'Payslips',       path: '/payslips',       icon: FileText }}   collapsed={ec} />}
            {/* Pay Contractors is an operational AP action (invoice → approve → ABA), not a financial
                report — surfaced as its own top-level item rather than buried inside Financials. */}
            {feat('enable_pay_contractors') && <NavItem item={{ label: 'Pay Contractors', path: '/financials/pay-contractors', icon: Receipt }} collapsed={ec} />}
            {feat('enable_reimbursements') && <NavItem item={{ label: 'Reimbursements', path: '/reimbursements', icon: Receipt }}    collapsed={ec} />}
            {feat('enable_mileage') && <NavItem item={{ label: 'Mileage',        path: '/mileage',        icon: Navigation2 }} collapsed={ec} />}
            {feat('enable_financials') && <ExpandableNavItem label="Financials" icon={DollarSign} basePath="/financials" exclude={['/financials/pay-contractors']} collapsed={ec}>
              <SubItem label="Dashboard"       path="/financials" />
              <SubItem label="Expenses"        path="/financials/expenses" />
              <SubGroup label="Reports">
                <SubItem label="Profit"          path="/financials/reports/profit" />
                <SubItem label="Staff Pay"        path="/financials/reports/staff-pay" />
                <SubItem label="Contractors"      path="/financials/reports/contractors" />
                <SubItem label="Client Funding"   path="/financials/reports/client-funding" />
                <SubItem label="Stakeholders"     path="/financials/reports/stakeholders" />
                <SubItem label="KM / Travel"      path="/financials/reports/km" />
                <SubItem label="Expenses"         path="/financials/reports/expenses" />
                <SubItem label="Audit"            path="/financials/reports/audit" />
              </SubGroup>
              <SubItem label="Configuration" path="/financials/config" />
            </ExpandableNavItem>}
          </DepartmentSection>
        )}

        {/* ── INSIGHTS ───────────────────────────────────────────────────── */}
        {isAdmin && (
          <DepartmentSection label="Insights" collapsed={ec}>
            {feat('enable_reports') && <NavItem item={{ label: 'Reports', path: '/reports/hub', icon: BarChart3 }} collapsed={ec} />}
            {/* Reminders & reviews (Google review requests, reminders) — for everyone: super admin (Rostery global) and orgs alike. */}
            {(user?.role === 'super_admin' || modules.automations === true) && <NavItem item={{ label: 'Reminders & reviews', path: '/automations', icon: Star }} collapsed={ec} />}
            {feat('enable_market_intel') && <NavItem item={{ label: 'Market Intel', path: '/market-intel', icon: LineChart }} collapsed={ec} />}
          </DepartmentSection>
        )}

        {/* ── COMMUNICATIONS ─────────────────────────────────────────────── */}
        <DepartmentSection label="Communications" collapsed={ec}>
          {/* A super admin outside the Super Admin panel is scoped to their own company, so this is
              that company's mail only. */}
          {['admin', 'office_user', 'super_admin'].includes(user?.role) && (
            <ExpandableNavItem label="Email" icon={Mail} basePath="/mail" collapsed={ec}>
              <EmailNav />
            </ExpandableNavItem>
          )}
          {messagingEnabled && (
            <NavItem item={{ label: 'Messages', path: '/messages', icon: MessageSquare }} collapsed={ec} />
          )}
          {user?.role === 'super_admin' && (
            <NavItem item={{ label: 'AI Receptionist', path: '/receptionist', icon: Bot }} collapsed={ec} />
          )}
          {user?.role !== 'super_admin' && modules.ai_receptionist === true && (
            <NavItem item={{ label: 'AI Receptionist', path: '/ai-receptionist', icon: Bot }} collapsed={ec} />
          )}
          {(user?.role === 'super_admin' || modules.ai_chatbot === true) && (
            <NavItem item={{ label: 'Website chat', path: '/chatbot', icon: MessageSquare }} collapsed={ec} />
          )}
        </DepartmentSection>

        {/* ── PLATFORM (super admin only: platform console + the marketing site's CMS) ── */}
        {user?.role === 'super_admin' && (
          <DepartmentSection label="Platform" collapsed={ec}>
            <NavItem item={{ label: 'Super Admin', path: '/superadmin', icon: ShieldCheck }} collapsed={ec} />
            <ExpandableNavItem label="Website CMS" icon={Globe} basePath="/website" collapsed={ec}>
              <SubItem label="Overview"          path="/website" />
              <SubItem label="Blog Posts"        path="/website/blog" />
              <SubItem label="Categories"        path="/website/blog/categories" />
              <SubItem label="Features"          path="/website/features" />
              <SubItem label="Testimonials"      path="/website/testimonials" />
              <SubItem label="FAQs"              path="/website/faqs" />
              <SubItem label="Pricing Plans"     path="/website/pricing" />
              <SubItem label="Pages"             path="/website/pages" />
              <SubItem label="How It Works"      path="/website/how-it-works" />
              <SubItem label="Integrations"      path="/website/integrations" />
              <SubItem label="Compliance Badges" path="/website/compliance" />
              <SubItem label="Social Proof"      path="/website/social-proof" />
              <SubItem label="Site Settings"     path="/website/settings" />
            </ExpandableNavItem>
          </DepartmentSection>
        )}

        {/* ── ADMIN ──────────────────────────────────────────────────────── */}
        {['admin', 'super_admin'].includes(user?.role) && (
          <DepartmentSection label="Admin" collapsed={ec}>
            <ExpandableNavItem label="Settings" icon={Settings} basePath="/settings" also={['/billing']} collapsed={ec}>
              <SubItem label="General"             path="/settings" />
              <SubItem label="Billing & Plan"      path="/billing" />
              <SubItem label="Branding"            path="/settings/branding" />
              <SubItem label="Features"            path="/settings/features" />
              <SubItem label="Invoice Settings"    path="/settings/invoices" />
              {mod('smart_match') && <SubItem label="Qualification Match" path="/settings/smart-match" />}
              <SubItem label="Prices"              path="/settings/prices" />
              {/* Price book, pay groups and allowances are baseline configuration on every
                  plan — a provider cannot bill or pay anyone without them, so they are
                  never gated. */}
              <SubGroup label="Pay Items">
                <SubItem label="Pay Groups" path="/settings/pay-groups" />
                <SubItem label="Pay Rates" path="/settings/pay-rates" />
                <SubItem label="Allowances" path="/settings/allowances" />
              </SubGroup>
              {mod('custom_roles') && <SubItem label="Roles"         path="/settings/roles" />}
              <SubItem label="Locations"     path="/settings/locations" />
              <SubItem label="Family Portal" path="/settings/family-portal" />
              <SubItem label="Send a notification" path="/settings?section=broadcast" />
              <SubItem label="AI & Automation" path="/settings/ai-automation" />
              {modules.ai_receptionist === true && (
                <SubItem label="AI Receptionist" path="/settings/ai-receptionist" />
              )}
            </ExpandableNavItem>
            {user?.role === 'super_admin' && (
              <ExpandableNavItem label="Compliance" icon={Scale} basePath="/compliance" collapsed={ec}>
                <SubItem label="Breach Register"     path="/compliance" />
                <SubItem label="Access Log"          path="/compliance?tab=access" />
                <SubItem label="Retention Policies"  path="/compliance?tab=retention" />
              </ExpandableNavItem>
            )}
            {feat('enable_fleet') && <NavItem item={{ label: 'Fleet', path: '/vehicles', icon: Car }} collapsed={ec} />}
            {mod('facilities') && (
              <NavItem item={{ label: 'Facilities', path: '/facilities', icon: Building2 }} collapsed={ec} />
            )}
            {feat('enable_activity_log') && <NavItem item={{ label: 'Activity Log', path: '/activity', icon: ClipboardList }} collapsed={ec} />}
            {/* Data import: org admins import into their OWN org (locked); super admin into any org. */}
            <NavItem item={{ label: 'Data import', path: '/migration', icon: Database }} collapsed={ec} />
            {/* Export Data: super-admin only — organisations don't get self-service export. */}
            {user?.role === 'super_admin' && (
              <NavItem item={{ label: 'Export Data', path: '/export-data', icon: HardDrive }} collapsed={ec} />
            )}
          </DepartmentSection>
        )}

      </nav>
    </motion.aside>
  );
}
